import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import React from 'react';

import { MarqueeRow, ShowcaseItem } from '@/components/home/ShowcaseMarquee';

const HOME = 'https://media.easy-peasy.ai/landing/home';
const MEDIA = 'https://media.easy-peasy.ai/landing/image';
const TPL = '/ai-image-generator/templates';

// Every card links to the template page carrying the exact prompt + model
// that produced it (catalogued in mediaTemplates.ts).
const ROW_A: ShowcaseItem[] = [
  {
    src: `${HOME}/img-fashion.webp`,
    aspect: '3/4',
    label: 'Editorial fashion',
    href: `${TPL}/editorial-fashion-portrait`,
  },
  {
    src: `${HOME}/img-perfume.webp`,
    aspect: '1/1',
    label: 'Product shots',
    href: `${TPL}/luxury-product-splash`,
  },
  {
    src: `${MEDIA}/hero-whale.webp`,
    aspect: '16/9',
    label: 'Surreal art',
    href: '/ai-images',
  },
  {
    src: `${HOME}/img-anime.webp`,
    aspect: '3/4',
    label: 'Anime art',
    href: `${TPL}/anime-key-visual`,
  },
  {
    src: `${HOME}/img-hummingbird.webp`,
    aspect: '4/3',
    label: 'Wildlife photo',
    href: `${TPL}/wildlife-macro-photo`,
  },
  {
    src: `${HOME}/img-castle.webp`,
    aspect: '9/16',
    label: 'Fantasy worlds',
    href: `${TPL}/fantasy-matte-painting`,
  },
  {
    src: `${HOME}/img-dessert.webp`,
    aspect: '1/1',
    label: 'Food editorial',
    href: `${TPL}/dark-food-editorial`,
  },
  {
    src: `${MEDIA}/hero-samurai.webp`,
    aspect: '16/9',
    label: 'Cinematic stills',
    href: '/ai-images',
  },
];

const ROW_B: ShowcaseItem[] = [
  {
    src: `${HOME}/img-neon.webp`,
    aspect: '9/16',
    label: 'Night scenes',
    href: `${TPL}/cinematic-neon-portrait`,
  },
  {
    src: `${HOME}/img-robot.webp`,
    aspect: '1/1',
    label: '3D characters',
    href: `${TPL}/3d-character-render`,
  },
  {
    src: `${HOME}/img-astronaut.webp`,
    aspect: '16/9',
    label: 'Concept art',
    href: `${TPL}/sci-fi-concept-art`,
  },
  {
    src: `${HOME}/img-headshot.webp`,
    aspect: '1/1',
    label: 'AI headshots',
    href: '/ai-headshot-generator',
  },
  {
    src: `${HOME}/img-watercolor.webp`,
    aspect: '3/4',
    label: 'Watercolor',
    href: `${TPL}/watercolor-cityscape`,
  },
  {
    src: `${HOME}/img-architecture.webp`,
    aspect: '3/4',
    label: 'Architecture',
    href: `${TPL}/futuristic-architecture-photo`,
  },
  {
    src: `${MEDIA}/hero-city.webp`,
    aspect: '16/9',
    label: 'Sci-fi worlds',
    href: '/ai-images',
  },
  {
    src: `${HOME}/img-sneaker.webp`,
    aspect: '1/1',
    label: 'Product ads',
    href: `${TPL}/product-deconstruction-shot`,
  },
  {
    src: `${HOME}/img-interior.webp`,
    aspect: '4/3',
    label: 'Interior design',
    href: '/ai-interior-design-generator',
  },
];

const ImageShowcaseRows = () => {
  const { t } = useLocale('ai-image-generator');

  return (
    <section id='showcase' className='relative overflow-hidden py-20 lg:py-28'>
      <div className='mx-auto mb-12 flex max-w-7xl flex-col items-start justify-between gap-6 px-4 sm:px-6 md:flex-row md:items-end lg:px-8'>
        <div>
          <h2 className='text-4xl font-extrabold tracking-tight text-white md:text-5xl'>
            {t('Every pixel here is AI')}
          </h2>
          <p className='mt-4 max-w-xl text-lg text-slate-400'>
            {t(
              'Untouched output from the generator. Click any image to open the exact prompt that made it.',
            )}
          </p>
        </div>
        <Link
          href='/ai-images'
          className='shrink-0 rounded-full border border-white/15 bg-white/5 px-6 py-3 font-semibold text-white transition-all hover:border-white/40 hover:bg-white/10'
        >
          {t('Open the generator')} →
        </Link>
      </div>

      <div className='flex flex-col gap-4'>
        <MarqueeRow items={ROW_A} duration={85} />
        <MarqueeRow items={ROW_B} duration={100} reverse />
      </div>
    </section>
  );
};

export default ImageShowcaseRows;
